import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";

const AmazonProductScraperContent = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 4 }}>
      {/* Headline */}
      <Typography variant="h4" fontWeight="bold" sx={{ fontSize: "1.75rem" }} gutterBottom>
        Effortlessly Extract Amazon Product Data
      </Typography>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Upload ASINs and Get a Clean, Structured CSV in Minutes
      </Typography>

      {/* Problem & Solution */}
      <Typography variant="body1" paragraph>
        Manually copying product details from Amazon is time-consuming and error-prone. Whether you're a researcher, dropshipper, or seller, it's frustrating and inefficient.
      </Typography>
      <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        With our ASIN Scraper, just upload a CSV of ASINs and get detailed product data – price, title, rating, image, and more – ready to use or analyze.
      </Typography>

      {/* Benefits */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={3}>
        Why Use the Amazon Product Scraper?
      </Typography>
      <ul style={{ textAlign: "left", display: "inline-block", listStyleType: 'none', paddingLeft: 0 }}>
        <li>📦 <strong>Batch Processing:</strong> Upload a full list of ASINs at once</li>
        <li>📊 <strong>Detailed Info:</strong> Title, Price, Rating, Image URL, & more</li>
        <li>⏱️ <strong>Save Time:</strong> Skip manual copying and formatting</li>
        <li>🌍 <strong>Multi-Market Support:</strong> Choose from 10+ Amazon marketplaces</li>
        <li>📥 <strong>Instant CSV Output:</strong> Ready-to-use product data file sent to your email</li>
      </ul>

      {/* How It Works */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        How It Works in 4 Simple Steps
      </Typography>
      <Box sx={{ 
        backgroundColor: '#f5f5f5', 
        p: 3, 
        borderRadius: 2,
        textAlign: 'left',
        display: 'inline-block'
      }}>
        <ol>
          <li><strong>Prepare</strong> a CSV file with ASINs (one per line)</li>
          <li><strong>Select</strong> your target Amazon marketplace</li>
          <li><strong>Upload</strong> the file and click "Scrape"</li>
          <li><strong>Receive</strong> the complete CSV via email in minutes</li>
        </ol>
      </Box>

      {/* Social Proof */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        Trusted by Amazon Sellers & Researchers
      </Typography>
      <Box sx={{ fontStyle: 'italic', mb: 3 }}>
        <Typography paragraph>
          "I used to spend hours collecting product info. Now I just upload a file and get everything in my inbox. Game changer!"
        </Typography>
        <Typography>- Raj M., Amazon FBA Seller</Typography>
      </Box>

      {/* FAQ Section */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Frequently Asked Questions
      </Typography>

      <Box textAlign="left" sx={{ display: 'inline-block' }}>
        <Typography variant="body1" fontWeight="bold">📁 What kind of file should I upload?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Just a CSV file with one ASIN per line. That’s it!
        </Typography>

        <Typography variant="body1" fontWeight="bold">🌍 Which Amazon countries are supported?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          You can select from Amazon US, UK, CA, IN, DE, FR, IT, JP, MX, and more.
        </Typography>

        <Typography variant="body1" fontWeight="bold">📬 How will I receive the data?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          You’ll get an email with a CSV file containing your results, usually in under 5 minutes.
        </Typography>
      </Box>

      {/* CTA */}
      <Box mt={4} mb={6}>
        <Typography variant="h6" paragraph sx={{ fontWeight: 'bold' }}>
          Ready to scrape Amazon like a pro?
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          size="large" 
          onClick={scrollToTop}
          sx={{ 
            fontSize: '1.1rem',
            py: 1.5,
            px: 4,
            fontWeight: 'bold'
          }}
        >
          Start Scraping ASINs Now →
        </Button>
        <Typography variant="caption" display="block" mt={1}>
          1 product scraped =  2 credits
        </Typography>
      </Box>
    </Container>
  );
};

export default AmazonProductScraperContent;
